import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCountdownOptions {
  /** 倒计时时长（秒） */
  seconds?: number;
  /** 倒计时结束回调 */
  onEnd?: () => void;
}

interface UseCountdownReturn {
  /** 当前倒计时数值 */
  count: number;
  /** 是否正在倒计时 */
  counting: boolean;
  /** 开始倒计时 */
  start: (seconds?: number) => void;
  /** 停止倒计时 */
  stop: () => void;
  /** 重置倒计时 */
  reset: () => void;
}

/**
 * 倒计时 Hook
 */
export const useCountdown = (options: UseCountdownOptions = {}): UseCountdownReturn => {
  const { seconds = 60, onEnd } = options;
  const [count, setCount] = useState(0);
  const [counting, setCounting] = useState(false);
  /** 全局定时器返回类型随环境变化，0 保留为未启动或已停止的哨兵值 */
  const timerRef = useRef<ReturnType<typeof setInterval> | 0>(0);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = 0;
    }
    setCounting(false);
  }, []);

  const reset = useCallback(() => {
    stop();
    setCount(0);
  }, [stop]);

  const onEndRef = useRef(onEnd);
  useEffect(() => { onEndRef.current = onEnd; }, [onEnd]);

  // count 归零时停止计时并触发回调
  useEffect(() => {
    if (count === 0 && counting) {
      stop();
      onEndRef.current?.();
    }
  }, [count, counting, stop]);

  const start = useCallback(
    (customSeconds?: number) => {
      stop();
      const total = customSeconds ?? seconds;
      setCount(total);
      setCounting(true);

      timerRef.current = setInterval(() => {
        setCount((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [seconds, stop]
  );

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    count,
    counting,
    start,
    stop,
    reset,
  };
};
